import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FaSpinner } from 'react-icons/fa';
import { GoFileZip } from 'react-icons/go';
import { BtnComponent } from '../../components/button';
import { useGroupChatContext } from '../../context/groupchatContext';
import {useGetNursingHomeGlobalDocumentsMessagesQuery, useGetNursingHomeLocationsQuery, useGetDocStatusListQuery, useGetNursingHomeChildDocumentsQuery } from '../../services/chatService';
import { TableListing } from '../../components/AppListing/TableListing';
import AttachmentContainer from '../groupChat/attachmentContainer';
import useSendPdfToChat from '../../hooks/useSendPdfToChat';
import Pagination from '../../components/pagination';
import { AdvanceSelect } from '../../components/reactHookFormFields/advanceSelectField';
import { DocumentMagnifyingGlassIcon, EyeIcon } from '@heroicons/react/24/outline';
import { DatePickerFeild } from '../../components/reactHookFormFields/datePicker';
import MainPageDialog from '../../components/dialog';
import { PdfViewer } from '../../components/pdfViewer';
import FoxitDialog from '../../components/Foxit/FoxitDialog';
import { formatBytes } from '../../components/common';
import icPdf from '../../assets/ic-doc/pdf.svg';
import icImage from '../../assets/ic-doc/image.svg';
import icTxt from '../../assets/ic-doc/text.svg';
import { imageFromUuid } from '../../helpers/utility';


const formatDateTimeDisplay = (dateStr) => {
    if (!dateStr) return "-";

    const formattedDate = moment(dateStr);

    if (!formattedDate.isValid()) return "-";

    return formattedDate.format('MM/DD/YYYY h:mm A');
};

const formatDocumentTitle = (title) => {
    if (!title) return 'Attachment';

    return title.charAt(0).toUpperCase() + title.slice(1);
};

const getDocumentIcon = (file) => {
    if (file?.mime_type === 'application/pdf' || file?.mime_type === 'pdf') {
        return icPdf;
    }

    if (['image/png', 'image/jpeg', 'image/gif'].includes(file?.mime_type)) {
        return imageFromUuid(file?.attachment_id);
    }

    if (file?.mime_type === 'text/plain') {
        return icTxt;
    }

    return icImage;
};

const SignedAttachmentCard = ({ file, index, totalFiles, openInEditor }) => {
    const [documentData, setDocumentData] = useState({ isOpen: false });
    const isZipFile = file?.mime_type === 'application/zip';
    const status = file?.status || 'signed';
    const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);

    const handleOpenFile = () => {
        if (isZipFile && file?.attachment_id) {
            const fileUrl = imageFromUuid(file.attachment_id);
            const downloadLink = document.createElement('a');
            downloadLink.href = fileUrl;
            downloadLink.download = file.title || 'download.zip';
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            return;
        }

        setDocumentData({ isOpen: true, file });
    };

    return (
        <>
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <div className="grid grid-cols-[52px_minmax(0,1fr)_auto] items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center">
                        {isZipFile ? (
                            <GoFileZip className="h-10 w-10 text-blue-500" />
                        ) : (
                            <img src={getDocumentIcon(file)} className="h-10 w-10 rounded-lg object-cover" alt="" />
                        )}
                    </div>

                    <div className="min-w-0">
                        <div className="flex min-w-0 items-start justify-between gap-2">
                            <p className="truncate text-base font-semibold text-black">
                                {formatDocumentTitle(file?.title)}
                            </p>
                            <span className="shrink-0 rounded-full border border-purple-500 bg-purple-50 px-3 py-0.5 text-xs font-medium text-purple-800">
                                {statusLabel}
                            </span>
                        </div>
                        <p className="mt-1 text-sm font-medium text-ethizo_primary">
                            Copy {index + 1} of {totalFiles}
                        </p>
                        <p className="mt-1 text-sm leading-5 text-gray-600">
                            Signed by {file?.signedBy || '-'} on {formatDateTimeDisplay(file?.signedAt)}
                        </p>
                        <p className="text-sm text-gray-600">
                            {file?.size ? formatBytes(file.size, 1) : '-'}
                        </p>
                    </div>

                    <button
                        type="button"
                        aria-label={`View ${file?.title || 'attachment'}`}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ethizo_primary transition hover:bg-ethizo_primary/10 focus:outline-none focus:ring-2 focus:ring-ethizo_primary/30"
                        onClick={handleOpenFile}
                    >
                        <EyeIcon className="h-6 w-6" aria-hidden />
                    </button>
                </div>
            </div>

            {(documentData.isOpen && openInEditor) ? (
                <FoxitDialog
                    foxitDialogState={documentData.isOpen}
                    setFoxitDialogState={setDocumentData}
                    file={documentData.file}
                    readOnly
                />
            ) : null}

            {(documentData.isOpen && !openInEditor && documentData.file?.mime_type !== 'application/zip') ? (
                <MainPageDialog
                    footer={false}
                    title={documentData.file?.title}
                    component={
                        <PdfViewer
                            data={{ files: [documentData.file] }}
                            uuid={documentData.file.attachment_id}
                            closeHandler={() => setDocumentData({})}
                        />
                    }
                    onCancelHandler={() => setDocumentData({})}
                />
            ) : null}
        </>
    );
};

const SignedDocumentsCell = ({ item, isOpenInEditor }) => {
    const [isOpen, setIsOpen] = useState(false);
    const signedDocumentUuid = item?.attachment_uuid;
    const status = item?.status ?? item?.attachment_status;
    const shouldShowButton = signedDocumentUuid && (String(status).toLowerCase() === 'signed' || item?.signed_attachment);
    const { data: childDocuments, isLoading, isFetching, isError } = useGetNursingHomeChildDocumentsQuery(
      {
        signed_document_uuid: signedDocumentUuid,
        limit: 10,
        page: 1,
      },
      {
        skip: !isOpen || !signedDocumentUuid,
      }
    );

    const files = (childDocuments?.data ?? []).map((document) => ({
      attachment_id: document.attachment_uuid,
      attachment_path: document.attachment_path,
      mime_type: document.mime_type,
      title: document.title,
      size: document.file_size,
      status: document.status,
      signedBy: document.status_updated_by || document.sender_name,
      signedAt: document.status_updated_at || document.created,
      patientName: document.patient_name,
    }));
    const totalFiles = childDocuments?.meta?.count || files.length;
    const patientName = files[0]?.patientName || item?.patient_name || '-';

    if (!shouldShowButton) {
      return null;
    }

    return (
      <>
        <button
          type="button"
          className="inline-flex min-h-[42px] items-center gap-1.5 rounded-md border border-ethizo_primary bg-white px-3 py-2 text-xs font-medium text-ethizo_primary shadow-sm transition hover:bg-ethizo_primary hover:text-white focus:outline-none focus:ring-2 focus:ring-ethizo_primary/30"
          onClick={() => setIsOpen(true)}
        >
          <EyeIcon className="h-4 w-4" aria-hidden />
          View Signed Attachment
        </button>

        {isOpen ? (
          <MainPageDialog
            title="Signed Attachments"
            footer={false}
            onCancelHandler={() => setIsOpen(false)}
            modalSize="md"
            contentCls="p-0 px-0"
          >
            <div className="rounded bg-white">
              <div className="border-b border-gray-200 px-5 py-3 text-gray-600">
                <p className="text-sm">{totalFiles} signed file(s) available</p>
                <p className="mt-2 text-sm">Patient: {patientName}</p>
              </div>

              <div className="min-h-[120px] space-y-3 px-5 py-4">
              {isLoading || isFetching ? (
                <div className="flex items-center justify-center py-10">
                  <FaSpinner className="animate-spin text-green-300-500" size={30} />
                </div>
              ) : isError ? (
                <div className="text-center text-red-500 py-8">
                  Unable to load signed attachments
                </div>
              ) : files.length ? (
                files.map((file, index) => (
                  <SignedAttachmentCard
                    key={`${file.attachment_id || file.attachment_path}-${index}`}
                    file={file}
                    index={index}
                    totalFiles={totalFiles}
                    openInEditor={isOpenInEditor(file?.mime_type)}
                  />
                ))
              ) : (
                <div className="text-center text-gray-400 py-8">
                  No signed attachments found
                </div>
              )}
              </div>
            </div>
          </MainPageDialog>
        ) : null}
      </>
    );
};

const GlobalDocuments = () => {
    const {
        practice: { id: practice_id, is_nursing: isNursingPractice },
        auth: authToken,
    } = JSON.parse(localStorage.getItem("auth"));
    const [currentPage, setCurrentPage] = useState(1);
    const postsPerPage = 20;

    const groupChatContext = useGroupChatContext();
  const {sendPdfToChat, isSending} = useSendPdfToChat();


    const { authData, selectedChat } = useMemo(() => ({
        authData: groupChatContext.authData,
        selectedChat: groupChatContext.selectedChat,
    }));

    const { control, setValue, reset, handleSubmit, formState: { errors }, } = useForm({
        defaultValues: {
            from_date: moment().subtract(6, 'days'),
            to_date: moment(),
        },
    });
    const { data: docStatusMeta } = useGetDocStatusListQuery({});
    const statusOptions = docStatusMeta?.document_statuses ?? [];

    const locationVal = useWatch({ control: control, name: "practice_location" });
    const statusVal = useWatch({ control: control, name: "status" });
    const fromDateVal = useWatch({ control: control, name: "from_date" });
    const toDateVal = useWatch({ control: control, name: "to_date" });

    useEffect(() => {
        if (fromDateVal && toDateVal && moment(toDateVal).isBefore(moment(fromDateVal), 'day')) {
            setValue("to_date", fromDateVal);
        }
    }, [fromDateVal, toDateVal, setValue]);

    const { data: nursingHomeLocations } = useGetNursingHomeLocationsQuery({ skip: authData?.is_provider });
    const { data: MessageList, isLoading, isFetching, refetch } = useGetNursingHomeGlobalDocumentsMessagesQuery({
        page: currentPage,
        limit: postsPerPage,
        location_id: locationVal?.map((location) => location.id)?.join(','),
        from_date: fromDateVal ? moment(fromDateVal).format('YYYY-MM-DD'): '',
        to_date: toDateVal ? moment(toDateVal).format('YYYY-MM-DD') : '',
        status: statusVal?.map((status) => status.value)?.join(','),
      },
      {
        refetchOnMountOrArgChange: true,
    });

    const isOpenInEditor = (mime_type) => {
        if (!mime_type) return false;
      
        return mime_type === 'pdf' || mime_type === 'application/pdf';
    };
    const resetFilters = () => {
        reset();
    }

    const formatDateTime = (dateStr) => {
        if (!dateStr) return "-";
      
        const date = new Date(dateStr);
      
        if (isNaN(date.getTime())) return "-";
      
        return date.toLocaleString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      };

    const getAttachmentFiles = (item) => {
        if (item?.attachment_uuid || item?.attachment_path) {
            return [{
                attachment_id: item.attachment_uuid,
                mime_type: item.mime_type,
                title: item.title,
                size: item.file_size,
            }];
        }

        if (!item?.message) return [];

        try {
            const parsed = JSON.parse(item.message);
            return parsed?.media || [];
        } catch {
            return [];
        }
    };
    const attachmentCellClass = 'align-top max-w-0 min-w-0 overflow-hidden';

    const tableCols = useMemo(() => {
        return [
          {
            title: 'Patient',
            key: 'patient_name',
            thClass: 'w-[12%]',
            tdClass: 'align-top max-w-0 min-w-0',
            colClass: 'text-left min-w-0 break-words',
            getData: (item) => (
              <span className="block font-semibold text-black">
                {item.patient_name || '-'}
              </span>
            ),
          },
          {
            title: 'Location',
            key: 'location_name',
            thClass: 'w-[14%]',
            tdClass: 'align-top max-w-0 min-w-0',
            colClass: 'text-left min-w-0 break-words',
            getData: (item) => (
              <span>{item.location_name || '-'}</span>
            ),
          },
          {
            title: 'Status',
            key: 'attachment_status',
            thClass: 'w-[10%]',
            tdClass: 'align-top max-w-0 min-w-0',
            colClass: 'text-left min-w-0',
            getData: (item) => {
              const status = item?.status ?? item?.attachment_status;
          
              const tooltip =
                status
                    ? `The document is marked as ${status} by ${
                        item?.status_updated_by || item?.reviewed_user_name || "-"
                    } on ${formatDateTime(item?.status_updated_at || item?.attachment_at)}`
                    : "Pending";
          
              return (
                <span title={tooltip}>
                  {status
                    ? status.charAt(0).toUpperCase() + status.slice(1)
                    : 'Pending'}
                </span>
              );
            },
          },
          {
            title: 'Sender',
            key: 'sender_name',
            thClass: 'w-[10%]',
            tdClass: 'align-top max-w-0 min-w-0',
            colClass: 'text-left min-w-0 break-words',
            getData: (item) => (
              <span>{item.sender_name || '-'}</span>
            ),
          },
          {
            title: 'Document Received',
            key: 'created',
            thClass: 'w-[12%]',
            tdClass: 'align-top max-w-0 min-w-0',
            colClass: 'text-left min-w-0 break-words',
            getData: (item) => (
              <span>{formatDateTime(item?.created)}</span>
            ),
          },
          {
            title: 'Status Updated Date',
            key: 'status_updated_at',
            thClass: 'w-[12%]',
            tdClass: 'align-top max-w-0 min-w-0',
            colClass: 'text-left min-w-0 break-words',
            getData: (item) => (
              <span>{formatDateTime(item?.status_updated_at || item?.attachment_at)}</span>
            ),
          },
          {
            title: 'Attachments',
            key: 'attachment',
            thClass: 'w-[15%]',
            tdClass: attachmentCellClass,
            colClass: 'text-left min-w-0 max-w-full',
            getData: (item) => {
              const files = getAttachmentFiles(item);

              if (!files.length) {
                return <span className="text-gray-400">No Attachment</span>;
              }

              return (
                <div className="w-full min-w-0 max-w-full overflow-hidden">
                  <AttachmentContainer
                    compact
                    isShowLinkToPatientChartWithImage={true}
                    data={{ files }}
                    openInEditor={isOpenInEditor(files[0]?.mime_type)}
                    callBackFun={sendPdfToChat}
                    showMarkReview={true}
                    messageId={item.id}
                    pid={item.pid}
                    onCloseAttachment={() => {
                      refetch();
                    }}
                  />
                </div>
              );
            },
          },
          {
            title: 'Signed Attachment',
            key: 'signed_attachment',
            thClass: 'w-[15%]',
            tdClass: attachmentCellClass,
            colClass: 'text-left min-w-0 max-w-full',
            getData: (item) => {
              return (
                <div className="w-full min-w-0 max-w-full overflow-hidden">
                  <SignedDocumentsCell item={item} isOpenInEditor={isOpenInEditor} />
                </div>
              );
            },
          },
        ];
      }, [isNursingPractice, practice_id, authData?.is_provider, refetch, sendPdfToChat]);

    const groupedListData = useMemo(() => {
        if (!MessageList?.data?.length) return [];
    
        return [
            {
                title: "Messages",
                data: MessageList.data,
            },
        ];
    }, [MessageList]);

    return (
        <div className="w-full text-gray-500 min-h-32 p-4 sm:p-6">
                    {isFetching ?
                        <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto absolute inset-0 outline-none focus:outline-none before:absolute before:flex before:w-full before:h-full z-[999]">
                            <FaSpinner className="animate-spin text-green-300-500 ml-1" size={40} />
                        </div>
                        : null
                    }

                    <div className='pt-4 pb-2 sticky top-[-10px] bg-white z-30'>
                        <div className='grid gap-4 grid-cols-2 md:grid-cols-6 pb-3'>
                            <div className="text-black">
                                <AdvanceSelect
                                    title="Location"
                                    control={control}
                                    setValue={setValue}
                                    name="practice_location"
                                    optionData={nursingHomeLocations}
                                    showLabel="name"
                                />
                            </div>
                            <div className="text-black">
                                <AdvanceSelect
                                    title="Status"
                                    control={control}
                                    setValue={setValue}
                                    name="status"
                                    optionData={statusOptions}
                                    showLabel="label"
                                />
                            </div>
                            <DatePickerFeild
                                title="From Date"
                                control={control}
                                name="from_date"
                                dateTime={false}
                                rules={{
                                    required: { value: true },
                                }}
                                errors={errors}
                            />
                            <DatePickerFeild
                                title="To Date"
                                control={control}
                                name="to_date"
                                dateTime={false}
                                minDate={fromDateVal ? new Date(fromDateVal) : undefined}
                                rules={{
                                    required: { value: true },
                                }}
                                errors={errors}

                            />
                            <div>
                                <BtnComponent text="Reset" btnType="default" onClick={resetFilters} />
                            </div>
                        </div>
                    </div>

                    {(MessageList?.data?.length > 0 || isFetching) ? (
                        <div className="mt-2 rounded-lg border border-gray-200">
                            <TableListing
                                groupedListData={groupedListData}
                                cols={tableCols}
                                loading={isFetching}
                                tableClassName="table-fixed w-full"
                                sectionRowClassName="bg-gray-100 border-b border-gray-200"
                                sectionCellClassName="py-2 px-3 text-sm text-left font-bold text-gray-800"
                            />
                            <div className="flex-shrink-0 pt-2 border-t">
                                <Pagination
                                    recordPerPage={postsPerPage}
                                    currentPage={currentPage}
                                    paginate={(page) => setCurrentPage(page)}
                                    totalRecords={MessageList?.meta?.count || 0} // IMPORTANT: ideally from API
                                    setCurrentPage={setCurrentPage}
                                />
                            </div>
                        </div>
                        
                    ) : (
                        <div
                            className="mt-2 flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white px-6 py-16 text-center shadow-sm sm:px-10"
                            role="status"
                            aria-live="polite"
                        >
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                                <DocumentMagnifyingGlassIcon className="h-8 w-8" aria-hidden />
                            </div>
                            <h3 className="mt-5 text-lg font-semibold text-gray-900">
                                No documents match your filters
                            </h3>
                            <div className="mt-6">
                                <BtnComponent
                                    text="Reset filters"
                                    btnType="default"
                                    onClick={resetFilters}
                                />
                            </div>
                        </div>
                    )}
        </div>
    );
};

export default GlobalDocuments;